/* eslint-disable  */

// Dependency imports
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';

// Local imports
import {
  useAddCameras,
  useDeleteConfigurationsCamera,
  useGetConfiguration,
  useUpdateVideoPath
} from 'api/sdk';
import { useSnack } from 'plugins/snack';
import { VideoMetadata } from 'schemas';
import {
  addCamera as addCameraAction,
  deleteCamera as deleteCameraAction,
  removeUploadController,
  selectCameras,
  selectConfig,
  setConfigFromApiResponse,
  updateVideoPath
} from 'store/config/configSlice';
import { RootState } from 'store';
import { useAppSelector } from 'hooks';
import { setPreventNavigation } from 'store/preventNavigation/preventNavigationSlice';
import {
  completeUploadSequence,
  decrementPendingUploads,
  decrementUpdatePathOperations,
  incrementUpdatePathOperations
} from 'store/train/videoUploadTrackingSlice';
import { activitiesSlice } from 'store/config/activities';

export const useVideoUpload = (onContinue?: () => void | undefined) => {
  const cameras = useSelector(selectCameras);
  const configId = useSelector(
    (state: RootState) => state.configuration.configuration
  );
  const activeUploads = useSelector(
    (state: RootState) => state.videoUploadTracking.activeUploads
  );
  const dispatch = useDispatch();
  const snack = useSnack();
  const [videoError, setError] = useState<string | null>(null);
  const {
    refetch,
    isLoading,
    data: getConfigurationData
  } = useGetConfiguration(
    {
      config_id: configId || ''
    },
    {
      query: {
        enabled: !!configId
      }
    }
  );
  const { mutateAsync } = useUpdateVideoPath();
  const { isLoading: isAddingCameras } = useAddCameras();
  const { mutateAsync: deleteCamera } = useDeleteConfigurationsCamera();
  const uploadControllers = useSelector(
    (state: RootState) => state.configV2.uploadControllers
  );

  // Upload video
  const handleUpload = async (
    videoPath: string,
    url: string,
    cameraId: string,
    meta: VideoMetadata
  ) => {
    try {
      console.log('Starting upload for camera:', cameraId);
      dispatch(incrementUpdatePathOperations(1));
      await mutateAsync({
        data: {
          metadata: meta,
          video_path: videoPath,
          filename: url
        },
        params: {
          camera_id: cameraId,
          config_id: configId || ''
        }
      });
      dispatch(
        updateVideoPath({
          cameraId,
          video_url: url
        })
      );
      console.log(
        'Decrementing uploads after successful upload for camera:',
        cameraId
      );
      dispatch(decrementPendingUploads());
      dispatch(decrementUpdatePathOperations());
      snack({
        message: 'Video uploaded successfully',
        severity: 'success'
      });
    } catch (error) {
      console.log(
        'Decrementing uploads after successful upload for camera:',
        cameraId
      );
      dispatch(decrementPendingUploads());
      dispatch(decrementUpdatePathOperations());
      snack({
        message: 'Failed to upload video',
        severity: 'error'
      });
    }
  };

  // Delete video
  const handleDelete = async (cameraId: string) => {
    Swal.fire({
      title: 'Delete Video',
      text: 'Are you sure you want to delete this video?',
      allowOutsideClick: false,
      confirmButtonText: 'Yes, delete it!',
      showCancelButton: true,
      confirmButtonColor: '#E33030',
      cancelButtonColor: '#102633',
      cancelButtonText: 'No, cancel!',
      focusCancel: true,
      customClass: {
        title: 'swal2-title',
        container: 'swal2-html-container',
        actions: 'swal2-actions'
      }
    }).then(async result => {
      if (result.isConfirmed) {
        try {
          dispatch(deleteCameraAction(cameraId));

          await deleteCamera({
            params: {
              camera_id: cameraId
            }
          });
          snack({
            message: 'Video deleted successfully',
            severity: 'success'
          });
        } catch (error) {
          console.error('Error deleting video:', error);
          snack({
            message: 'Failed to delete video, please try again',
            severity: 'error'
          });
        }
      }
    });
  };

  const handleDeletUpload = async (cameraId: string, refresh: any) => {
    try {
      await deleteCamera({
        params: {
          camera_id: cameraId
        }
      });
    } catch (error) {
      console.error('Error deleting video:', error);
    }
  };
  const refresh = useCallback(() => {
    refetch().then(response => {
      response.data &&
        dispatch(
          setConfigFromApiResponse({
            config: response.data.data
          })
        );
    });
  }, [dispatch, refetch, configId]);

  // Cancel the upload
  const cancelUpload = useCallback(
    async (cameraId: string) => {
      try {
        // Get and abort the XHR request from Redux state
        const controller = uploadControllers[cameraId];
        if (controller) {
          controller.abort(); // This will stop the upload
          dispatch(removeUploadController(cameraId));
          dispatch(decrementPendingUploads());
          dispatch(decrementUpdatePathOperations());
          dispatch(completeUploadSequence());
          handleDeletUpload(cameraId, refresh);
        }

        // Remove the camera from state
        dispatch(deleteCameraAction(cameraId));

        // Reset navigation prevention if needed
        const remainingUploadingVideos =
          cameras?.filter(
            cam => cam.id !== cameraId && cam.status === 'uploading'
          ) || [];

        if (remainingUploadingVideos.length === 0) {
          dispatch(setPreventNavigation(false));
        }

        snack({
          message: 'Upload cancelled successfully',
          severity: 'info'
        });
      } catch (error) {
        snack({
          message: 'Failed to cancel upload',
          severity: 'error'
        });
      }
    },
    [dispatch, cameras, snack, uploadControllers, activeUploads]
  );

  const handleContinue = useCallback(async () => {
    setError(null);
    refetch().then(response => {
      response.data &&
        dispatch(
          setConfigFromApiResponse({
            config: response.data.data
          })
        );
    });
    // onContinue?.();
  }, [cameras, setError]);

  useEffect(() => {
    refresh();
  }, [configId]);

  return {
    cameras,
    configId,
    isAddingCameras,
    isLoading,
    handleUpload,
    handleDelete,
    // handleAddCamera,
    videoError,
    getConfigurationData,
    setError,
    handleContinue,
    cancelUpload,
    refresh
  };
};
